import { useEffect, useRef, useState } from "react";
import { motion } from "framer-motion";
import { Zap, Music, RotateCcw, Volume2, VolumeX, Save } from "lucide-react";
import { saveLoop } from "@/lib/api";

/**
 * Jixxeo Vibes — a real playable mini-PWA embedded inside Jixxeo.
 * Tap pads to trigger neon-styled synth notes (Web Audio API), toggle a
 * pulsing beat, and record a tiny loop. No dependencies.
 */

const PADS = [
  { note: 261.63, label: "C", color: "#00F0FF", key: "a" },
  { note: 293.66, label: "D", color: "#39FF14", key: "s" },
  { note: 329.63, label: "E", color: "#FFE300", key: "d" },
  { note: 349.23, label: "F", color: "#FF003C", key: "f" },
  { note: 392.00, label: "G", color: "#00F0FF", key: "g" },
  { note: 440.00, label: "A", color: "#39FF14", key: "h" },
  { note: 493.88, label: "B", color: "#FFE300", key: "j" },
  { note: 523.25, label: "C+", color: "#FF003C", key: "k" },
];

export default function JixxeoVibes() {
  const [audioReady, setAudioReady] = useState(false);
  const [muted, setMuted] = useState(false);
  const [beatOn, setBeatOn] = useState(false);
  const [bpm, setBpm] = useState(110);
  const [recording, setRecording] = useState(false);
  const [loop, setLoop] = useState([]);
  const [playingLoop, setPlayingLoop] = useState(false);
  const [lastPad, setLastPad] = useState(null);
  const ctxRef = useRef(null);
  const beatTimerRef = useRef(null);
  const loopTimerRef = useRef(null);
  const recordStartRef = useRef(0);

  const ensureCtx = () => {
    if (!ctxRef.current) {
      const AC = window.AudioContext || window.webkitAudioContext;
      ctxRef.current = new AC();
      setAudioReady(true);
    }
    if (ctxRef.current.state === "suspended") ctxRef.current.resume();
    return ctxRef.current;
  };

  const playNote = (freq, dur = 0.35) => {
    if (muted) return;
    const ctx = ensureCtx();
    const o = ctx.createOscillator();
    const g = ctx.createGain();
    o.type = "sawtooth";
    o.frequency.value = freq;
    const filter = ctx.createBiquadFilter();
    filter.type = "lowpass";
    filter.frequency.value = 1400;
    o.connect(filter); filter.connect(g); g.connect(ctx.destination);
    const now = ctx.currentTime;
    g.gain.setValueAtTime(0, now);
    g.gain.linearRampToValueAtTime(0.16, now + 0.01);
    g.gain.exponentialRampToValueAtTime(0.0001, now + dur);
    o.start(now);
    o.stop(now + dur + 0.05);
  };

  const kickDrum = () => {
    if (muted) return;
    const ctx = ensureCtx();
    const o = ctx.createOscillator();
    const g = ctx.createGain();
    o.frequency.setValueAtTime(140, ctx.currentTime);
    o.frequency.exponentialRampToValueAtTime(40, ctx.currentTime + 0.14);
    g.gain.setValueAtTime(0.35, ctx.currentTime);
    g.gain.exponentialRampToValueAtTime(0.001, ctx.currentTime + 0.16);
    o.connect(g); g.connect(ctx.destination);
    o.start(); o.stop(ctx.currentTime + 0.18);
  };

  const hit = (idx) => {
    const p = PADS[idx];
    setLastPad(idx);
    playNote(p.note);
    setTimeout(() => setLastPad(null), 180);
    if (recording) {
      const t = performance.now() - recordStartRef.current;
      setLoop((prev) => [...prev, { idx, t }]);
    }
  };

  // Keyboard
  useEffect(() => {
    const onKey = (e) => {
      const i = PADS.findIndex((p) => p.key === e.key.toLowerCase());
      if (i >= 0) { e.preventDefault(); hit(i); }
      if (e.key === "b") setBeatOn((b) => !b);
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [recording, muted]);

  // Beat
  useEffect(() => {
    if (beatTimerRef.current) clearInterval(beatTimerRef.current);
    if (beatOn) {
      const interval = 60000 / bpm / 2;
      beatTimerRef.current = setInterval(() => kickDrum(), interval);
    }
    return () => beatTimerRef.current && clearInterval(beatTimerRef.current);
  }, [beatOn, bpm, muted]);

  const startRecord = () => {
    setLoop([]);
    setRecording(true);
    recordStartRef.current = performance.now();
    setTimeout(() => setRecording(false), 4000); // 4 second loop
  };

  const playLoop = () => {
    if (!loop.length || playingLoop) return;
    setPlayingLoop(true);
    const runOnce = () => {
      loop.forEach((n) => setTimeout(() => hit(n.idx), n.t));
    };
    runOnce();
    // loop again after 4.2s
    loopTimerRef.current = setInterval(runOnce, 4200);
  };
  const stopLoop = () => {
    setPlayingLoop(false);
    if (loopTimerRef.current) clearInterval(loopTimerRef.current);
  };

  const clearLoop = () => { stopLoop(); setLoop([]); };

  const [saving, setSaving] = useState(false);
  const saveLoopToProfile = async () => {
    if (!loop.length) return;
    const name = window.prompt("Name this loop:", "My Vibe");
    if (!name) return;
    setSaving(true);
    try {
      await saveLoop(name, bpm, loop);
      // eslint-disable-next-line no-alert
      window.alert("Saved to your profile ✓");
    } catch (e) {
      window.alert("Sign in to save loops");
    } finally { setSaving(false); }
  };

  return (
    <div className="min-h-screen bg-[#050505] text-white p-6 md:p-10 relative overflow-hidden">
      {/* backdrop */}
      <div className="absolute inset-0 pointer-events-none opacity-40" style={{
        background: "radial-gradient(ellipse at 30% 10%, rgba(0,240,255,0.18), transparent 60%), radial-gradient(ellipse at 80% 80%, rgba(57,255,20,0.12), transparent 60%)"
      }} />
      <div className="absolute inset-0 pointer-events-none opacity-25" style={{
        backgroundImage: "linear-gradient(rgba(0,240,255,0.09) 1px, transparent 1px), linear-gradient(90deg, rgba(0,240,255,0.09) 1px, transparent 1px)",
        backgroundSize: "40px 40px",
      }} />

      <div className="relative max-w-3xl mx-auto">
        {/* header */}
        <div className="flex items-center gap-3 mb-6">
          <span className="w-11 h-11 rounded-xl bg-[#00F0FF] flex items-center justify-center shadow-[0_0_24px_rgba(0,240,255,0.5)]">
            <Music className="w-6 h-6 text-black" strokeWidth={3} />
          </span>
          <div>
            <div className="mono text-[10px] uppercase tracking-[0.25em] text-[#00F0FF]">// jixxeo apps · demo pwa</div>
            <h1 className="font-heading font-black text-3xl md:text-4xl uppercase tracking-tighter leading-none">
              Jixxeo Vibes
            </h1>
          </div>
          <button onClick={() => setMuted((m) => !m)} className="ml-auto w-10 h-10 rounded-full border border-white/15 hover:border-[#00F0FF]/60 flex items-center justify-center">
            {muted ? <VolumeX className="w-4 h-4" /> : <Volume2 className="w-4 h-4 text-[#00F0FF]" />}
          </button>
        </div>
        <p className="text-white/70 text-sm max-w-lg">
          Tap the pads or use the keys <span className="mono text-[#39FF14]">a s d f g h j k</span> to jam.
          Press <span className="mono text-[#FFE300]">b</span> to toggle the beat.
        </p>

        {/* pads */}
        <div className="grid grid-cols-4 gap-3 md:gap-4 mt-8">
          {PADS.map((p, i) => (
            <motion.button
              key={p.label}
              whileTap={{ scale: 0.94 }}
              onClick={() => hit(i)}
              className="aspect-square rounded-2xl relative overflow-hidden border transition-shadow duration-200"
              style={{
                background: `linear-gradient(160deg, ${p.color}22, ${p.color}05)`,
                borderColor: lastPad === i ? p.color : `${p.color}55`,
                boxShadow: lastPad === i ? `0 0 40px ${p.color}, 0 0 100px ${p.color}44` : `0 0 0 transparent`,
              }}
            >
              <div className="absolute inset-0 flex flex-col items-center justify-center">
                <span className="font-heading font-black text-4xl md:text-5xl uppercase" style={{ color: p.color }}>
                  {p.label}
                </span>
                <span className="mono text-[10px] uppercase tracking-widest text-white/40 mt-1">key · {p.key}</span>
              </div>
            </motion.button>
          ))}
        </div>

        {/* controls */}
        <div className="mt-8 grid md:grid-cols-2 gap-3">
          <div className="rounded-2xl border border-white/10 bg-white/[0.03] p-4">
            <div className="mono text-[10px] uppercase tracking-widest text-white/50 mb-2">// beat</div>
            <div className="flex items-center gap-3">
              <button onClick={() => setBeatOn((b) => !b)} className={`px-4 py-2 rounded-full mono text-[10px] uppercase tracking-widest font-bold ${beatOn ? "bg-[#FF003C] text-black" : "border border-white/15 text-white/80"}`}>
                {beatOn ? "Stop" : "Start"}
              </button>
              <input type="range" min="60" max="180" value={bpm} onChange={(e) => setBpm(+e.target.value)} className="flex-1 accent-[#00F0FF]" />
              <span className="mono text-xs text-[#00F0FF]">{bpm} BPM</span>
            </div>
          </div>

          <div className="rounded-2xl border border-white/10 bg-white/[0.03] p-4">
            <div className="mono text-[10px] uppercase tracking-widest text-white/50 mb-2">// looper (4s)</div>
            <div className="flex items-center gap-2 flex-wrap">
              <button onClick={startRecord} disabled={recording}
                className={`px-3 py-2 rounded-full mono text-[10px] uppercase tracking-widest font-bold ${recording ? "bg-[#FF003C] text-black animate-pulse" : "bg-[#FFE300] text-black hover:bg-white"}`}>
                {recording ? "Recording…" : "Record"}
              </button>
              <button onClick={playingLoop ? stopLoop : playLoop} disabled={!loop.length}
                className="px-3 py-2 rounded-full mono text-[10px] uppercase tracking-widest border border-[#39FF14]/40 text-[#39FF14] hover:bg-[#39FF14]/10 disabled:opacity-30">
                {playingLoop ? "Stop" : "Play"}
              </button>
              <button onClick={clearLoop} disabled={!loop.length}
                className="px-3 py-2 rounded-full mono text-[10px] uppercase tracking-widest border border-white/15 text-white/60 hover:text-white disabled:opacity-30 flex items-center gap-1">
                <RotateCcw className="w-3 h-3" /> Clear
              </button>
              <button onClick={saveLoopToProfile} disabled={!loop.length || saving}
                className="px-3 py-2 rounded-full mono text-[10px] uppercase tracking-widest bg-[#00F0FF] text-black font-bold hover:bg-white disabled:opacity-30 flex items-center gap-1">
                <Save className="w-3 h-3" /> {saving ? "Saving…" : "Save"}
              </button>
              <span className="mono text-[10px] text-white/40 ml-auto">{loop.length} notes</span>
            </div>
          </div>
        </div>

        {!audioReady && (
          <div className="mt-6 mono text-[10px] uppercase tracking-widest text-[#FFE300] flex items-center gap-2">
            <Zap className="w-3 h-3" /> tap a pad to unlock audio
          </div>
        )}
        <div className="mt-8 mono text-[10px] uppercase tracking-widest text-white/40 flex items-center gap-2">
          <span className="w-2 h-2 rounded-full bg-[#39FF14]" /> built with jixxeo apps · web audio api · no install
        </div>
      </div>
    </div>
  );
}
